import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { ApiPrOverrideService } from '@core-mkt/services/api-pr-override/api-pr-override.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import type {
  WebinarScheduleContent,
  WebinarScheduleResponse,
} from '@core-mkt/shared/components/webinar-schedule/webinar-schedule';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, shareReplay, timeout } from 'rxjs/operators';
import { BrandConfigurationService } from '../configuration/brand-configuration.service';
import { CouponAggregationService } from '../coupon-aggregation/coupon-aggregation.service';
import { CouponQueryResolverService } from '../coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '../coupon-query-resolver/coupon-use-cases';
import type { HandleErrorError } from '../error-handler/error-handler';
import { ErrorHandlerService } from '../error-handler/error-handler.service';
import type { SyncUserResponse } from './sync-user-response';
import type { TransferTokenReponse } from './tranfer-token-response';
import type { XGritApiBase, XGritApiConfig } from './xgrit-api-config';
import type {
  ProductCodeLists,
  XGritPurchaseReceiptPriceResponse,
  XgritPurchaseReceiptMultipriceResponse,
} from './xgrit-purchase-receipt-price-response';

/**
 * A coupon entry as it appears inside a `lineItemList` entry. xGrit varies which field carries the
 * code between endpoints, so all three are optional and readers fall back across them.
 */
interface XGritCouponEntry {
  couponCode?: string;
  code?: string;
  name?: string;
}

/**
 * The coupon-bearing subset of a `lineItemList` entry.
 *
 * `XGritPurchaseReceiptPriceResponse.lineItemList` stays `any[]`: ~30 call sites across the product,
 * bundle and pricing services reach into product/pricing fields that were never modelled, so
 * narrowing the array wholesale is a separate refactor. This types only what the coupon reader below
 * touches, at the untyped API boundary.
 */
interface XGritLineItemCoupons {
  couponList?: XGritCouponEntry[];
  displayCouponList?: XGritCouponEntry[];
}

/** Product category entry returned inside a Craft product entry's `*ProductCategory` arrays. */
interface XGritProductCategory {
  aceCategoryCode?: string;
  title?: string;
}

@Injectable({
  providedIn: 'root',
})
export class XGritApiService {
  checkoutUrl: string;
  apiConfig: XGritApiConfig;
  apiBaseParams: XGritApiBase;

  globalCDIQueryParam: string;
  globalSalesQueryParam: string;

  constructor(
    readonly httpClient: HttpClient,
    private bfs: BrandConfigurationService,
    private errorHandler: ErrorHandlerService,
    private couponQueryResolverService: CouponQueryResolverService,
    @Optional() private couponAggregationService: CouponAggregationService,
    private apiPrOverrideService: ApiPrOverrideService,
  ) {
    this.globalCDIQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);
    this.globalSalesQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.SALES);

    const env = new EnvService();

    if (env.get.xgritCheckoutUrl) {
      this.checkoutUrl = env.get.xgritCheckoutUrl;
    }

    if (env.get.xgritApiConfig) {
      this.apiConfig = env.get.xgritApiConfig;

      // Apply api_pr override for non-production environments
      const overrideUrl = this.apiPrOverrideService.getOverrideUrl();
      if (overrideUrl) {
        this.apiConfig.url = overrideUrl;
      }

      this.apiBaseParams = {
        appType: this.apiConfig.baseParams.appType,
        platform: this.apiConfig.baseParams.platform,
        brandId: this.apiConfig.baseParams.brandId,
      };
    }
  }

  /**
   * constructParamString computes query string parameters based on the keys and values of an object
   *
   * @param {object} params - An object where each key and value pair will be turned into a query parameter
   * @return {string} - an object containing information about the product, or an error if one occurred
   *
   * @example
   *
   *     constructParamString({ productId: 'ufZk2XuZeNr8kAB', courseId: '4PleoXKYfKEhquja', coupon: 'CP10', state 'GA' })
   *
   *     returns, productId=FufZk2XuZeNr8kAB&courseId=4PleoXKYfKEhquja&coupon=CP10&state=GA
   *
   */
  public constructParamString(params: object): string {
    const paramString = Object.keys(params).reduce((paramAccu, paramKey) => {
      if (params[paramKey]) {
        if (paramAccu === '') {
          paramAccu += `${paramKey}=${params[paramKey]}`;
        } else {
          paramAccu += `&${paramKey}=${params[paramKey]}`;
        }
      }
      return paramAccu;
    }, '');

    return paramString;
  }

  /**
   * getCheckoutUrl computes an xGrit checkout url for an xGrit product.
   *
   * @param {string} productId - The id of the product
   * @param {string} courseId - The id of the course
   * @param {string} appType - A string declaring whether the product is DE, DD, RE, etc.
   * @param {boolean} disableRsa - A boolean value that determines if RSA should be disabled on a product checkout
   * @param {string} coupon - A coupon that supports this course
   * @param {string} state - The state that the checkout should display on the checkout
   * @param {string} bundleCoupon - A special kind of coupon for bundling upsells
   * @return {string} - An xGrit checkout url
   *
   * @example
   *
   *     getCheckoutUrl('ufZk2XuZeNr8kAB', '4PleoXKYfKEhquja', 'DRV', 'CP10', 'GA')
   *
   *     returns, https://checkout-stage.driversed.com/checkout?productId=FufZk2XuZeNr8kAB&courseId=4PleoXKYfKEhquja&coupon=CP10&state=GA&ace=cd99
   */
  public getCheckoutUrl(
    productId: string,
    courseId: string,
    appType?: string,
    disableRsa = false,
    coupon?: string,
    state?: string,
    bundleCoupon?: string,
  ): string {
    const cdiParamKey = this.bfs.cdiParamKey;
    const cdiParamValue = this.bfs.cdiParamValueWhenEnabled;

    // Use the provided coupon parameter directly
    const couponString = coupon;

    const params = {
      productId,
      courseId,
      // Include all collected coupons in the appropriate parameter
      [this.globalCDIQueryParam]: couponString,
      bundleCoupon,
      state,
    };

    if (!disableRsa && (appType === 'DE' || appType === 'DD')) {
      params[cdiParamKey] = cdiParamValue;
    }

    const paramString = this.constructParamString(params);

    return `${this.checkoutUrl}?${paramString}`;
  }

  /**
   * Extracts actually applied coupon codes from API response
   * Only includes coupons that the API actually applied based on hierarchy rules
   */
  public getAppliedCouponsFromResponse(apiResponse: XGritPurchaseReceiptPriceResponse): string[] {
    // Use couponList (all applied coupons) instead of displayCouponList (only visible coupons)
    // `lineItemList` is untyped (`any[]`) on the shared response interface; narrow just the coupon
    // shape here rather than retyping the array for its ~30 unrelated call sites.
    const lineItem = apiResponse?.lineItemList?.[0] as XGritLineItemCoupons | undefined;
    const couponSource: XGritCouponEntry[] = lineItem?.couponList ?? lineItem?.displayCouponList ?? [];

    if (couponSource.length === 0) {
      return [];
    }

    return couponSource
      .map((coupon) => coupon.couponCode || coupon.code || coupon.name)
      .filter((code): code is string => Boolean(code));
  }

  /**
   * Enhanced checkout URL that includes only API-applied coupons
   * Use this when you have API response data available
   */
  public getCheckoutUrlWithAppliedCoupons(
    productId: string,
    courseId: string,
    apiResponse: XGritPurchaseReceiptPriceResponse,
    appType?: string,
    disableRsa = false,
    state?: string,
    bundleCoupon?: string,
  ): string {
    let appliedCoupons = this.getAppliedCouponsFromResponse(apiResponse);

    // Fallback: if we can't extract applied coupons from API response,
    // collect all available coupons (better than no coupons)
    if (appliedCoupons.length === 0 && this.couponAggregationService) {
      const couponCollectionRequest = this.couponAggregationService.collectAllCoupons();
      appliedCoupons = this.couponAggregationService.getAllCouponCodes(couponCollectionRequest);
    }

    const couponString = appliedCoupons.length > 0 ? appliedCoupons.join(',') : undefined;

    return this.getCheckoutUrl(productId, courseId, appType, disableRsa, couponString, state, bundleCoupon);
  }

  /**
   * handleError an error handler for an Observable.
   *
   * @param {any} error - an error
   *
   */
  public handleError<T>(result?: T) {
    return (error: Partial<HandleErrorError> | string | undefined): Observable<T> => {
      this.errorHandler.handleError(error);
      return of(result);
    };
  }

  /**
   * getProductsInfo calls xGrit's 'product-course' endpoint which gets information on products.
   *
   * @param {string[]} productIdList - an array of productIds, not courseIds, to get product information on.
   * @return {Observable<any>} - an object containing information about the product, or an error if one occurred
   *
   * @example
   *
   *     getProductsInfo(['B93xTL1FzpYhKdJ1'])
   */
  public getProductsInfo(productIdList: string[]): Observable<unknown> {
    productIdList.push(''); // Always push an extra empty string at the end of productIdList since xGrit's 'product-course' endpoint requires an array of products.

    const callParams = {
      ...this.apiBaseParams,
      productIdList,
    };

    return this.httpClient
      .get<unknown>(this.apiConfig.url + 'product-course', {
        params: callParams,
      })
      .pipe(timeout(5000), catchError(this.handleError<unknown>({})));
  }

  /**
   * getProductPriceInfo calls xGrit's 'purchase-receipt/price' endpoint. This implementation of 'purchase-receipt/price' is meant to be a convenience method for getting a product's price information with a coupon applied.
   *
   * @param {string} productId - the product id, not a course id, relating to the product that you would like to get pricing information on.
   * @param {string} coupon - the coupon id of a coupon that can be a applied to the supplied product.
   * @return {Observable<XGritPurchaseReceiptPriceResponse>} - an object containing information about the product, or an error if one occurred
   *
   * @example
   *
   *     getProductPriceInfo('B93xTL1FzpYhKdJ1')
   */

  public getProductPriceInfo(productId: string, coupon?: string): Observable<XGritPurchaseReceiptPriceResponse> {
    let productIdList = [];
    let couponCodeList: string[] = [];
    let currentUrl = '';
    productIdList = productIdList.concat([productId]);
    if (coupon) {
      // Split comma-separated coupons into individual array elements
      const individualCoupons = coupon
        .split(',')
        .map((c) => c.trim())
        .filter((c) => Boolean(c));
      couponCodeList = couponCodeList.concat(individualCoupons);
    }

    // COUPON OVERHAUL: Enhance single product calls with all available coupons too!
    if (this.couponAggregationService) {
      const couponCollectionRequest = this.couponAggregationService.collectAllCoupons();
      const allAvailableCoupons = this.couponAggregationService.getAllCouponCodes(couponCollectionRequest);
      currentUrl = couponCollectionRequest.url; // Get current page URL for ambassador tracking

      // Merge existing coupon with all available coupons
      const combinedCoupons = [...new Set([...couponCodeList, ...allAvailableCoupons])];
      couponCodeList = combinedCoupons;
    }

    const callParams = {
      ...this.apiBaseParams,
      'productIdList[]': productIdList,
      'couponCodeList[]': couponCodeList,
      ...(currentUrl && { url: currentUrl }), // Include URL for ambassador coupon tracking
    };

    return this.httpClient
      .get<XGritPurchaseReceiptPriceResponse>(this.apiConfig.url + 'purchase-receipt/price', {
        params: {
          ...callParams,
        },
        headers: {
          Authorization: this.apiConfig.aceauth,
        },
      })
      .pipe(catchError(this.handleError<XGritPurchaseReceiptPriceResponse>({} as XGritPurchaseReceiptPriceResponse)));
  }

  /**
   * queryBuilder translate a complex object to a query parameter form.
   *
   * @param {object} objectParams - complex object to be sent over an http get request.
   * @returns {string} - a query parameters string representation of the complex object.
   */
  private queryBuilder(objectParams: Record<string, unknown>, prefix?: string): string {
    const queryParams: string[] = [];
    for (const property in objectParams) {
      if (Object.prototype.hasOwnProperty.call(objectParams, property)) {
        const key = prefix ? `${prefix}[${property}]` : property;
        const value = objectParams[property];
        queryParams.push(
          value !== null && typeof value === 'object'
            ? this.queryBuilder(value as Record<string, unknown>, key)
            : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
        );
      }
    }
    return queryParams.join('&');
  }

  /**
   * getMultiProductPriceInfo calls xgrit's 'purchase-receipt/multiprice' endpoint.
   * This implementation of 'purchase-receipt/multiprice' is meant to be a convience method for getting a product's price information with a coupon applied.
   *
   * @param {ProductCodeLists[]} priceSetList - array with objects containing product id and coupon code, both stored as unique elements of an array.
   * @return {Observable<XgritPurchaseReceiptMultipriceResponse>} - an object containing information about the product, or an error if one occurred
   *
   * @example
   *
   *   priceSetList = [
   *     {
   *       "productIdList": ["B93xTL1FzpYhKdJ1"],
   *       "couponCodeList": ["CP7"]
   *     },
   *     {
   *       "productIdList": ["jj51YsV07s"],
   *       "couponCodeList": ["ADEAW0"]
   *     },
  }
   *   ];
   *   getMultiProductPriceInfo(priceSetList);
   */
  public getMultiProductPriceInfo(
    priceSetList: ProductCodeLists[],
  ): Observable<XgritPurchaseReceiptMultipriceResponse> {
    // COUPON OVERHAUL: Automatically enhance all multiprice calls with comprehensive coupon collection
    const enhancedPriceSetList = this.enhancePriceSetListWithAllCoupons(priceSetList);

    const requestBody = {
      ...this.apiBaseParams,
      priceSetList: enhancedPriceSetList,
    };

    return this.httpClient
      .post<XgritPurchaseReceiptMultipriceResponse>(this.apiConfig.url + 'purchase-receipt/multiprice', requestBody, {
        headers: {
          Authorization: this.apiConfig.aceauth,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        map((response) => response),
        catchError(
          this.handleError<XgritPurchaseReceiptMultipriceResponse>({} as XgritPurchaseReceiptMultipriceResponse),
        ),
      );
  }

  /**
   * Retrieves any available product category from the product entry
   * This handles any type of product category (mortgage, insurance, real estate, etc.)
   *
   * @param productEntry - The product entry to check for categories
   * @returns The first available category found, or undefined if none exists
   */
  private getProductCategory(
    productEntry: CraftXGritProductEntry | CraftXGritProductEntry[],
  ): XGritProductCategory[] | undefined {
    // List of possible category properties to check
    const categoryProps = [
      'mortgageProductCategory',
      'insuranceProductCategory',
      'aceRealEstateProductCategory',
      'productCategory',
    ];

    // Return the first category property that exists and has data
    // Craft emits these category arrays per vertical and they are absent from the entry interface,
    // so index through an unknown-valued record rather than widening CraftXGritProductEntry.
    const entry = productEntry as unknown as Record<string, unknown>;
    for (const prop of categoryProps) {
      const value = entry[prop];
      if (Array.isArray(value) && value.length > 0) {
        return value as XGritProductCategory[];
      }
    }

    return undefined;
  }

  /**
   * Retrieves the webinar schedule for a list of course IDs.
   *
   * @param courseIdList - An array of objects containing course IDs to fetch the webinar schedule for.
   * @param productEntry - Optional product entry to determine the category.
   * @returns An observable that emits an array of `WebinarScheduleResponse` objects.
   *
   * The method constructs a set of parameters including the course IDs, a start date, and an end date,
   * and sends an HTTP GET request to the API endpoint to fetch the webinar schedule. The request includes
   * authorization headers. Errors during the HTTP request are caught and handled.
   */
  public getWebinarSchedule(
    courseIdList: WebinarScheduleContent[],
    productEntry: CraftXGritProductEntry | CraftXGritProductEntry[],
    webinarTitle?: string,
    webinarContent?: string,
  ): Observable<WebinarScheduleResponse[]> {
    const courseIds: string[] = [];
    courseIdList.forEach((param) => {
      if (param.courseid && param.courseid.length > 0) {
        courseIds.push(param.courseid);
      }
    });

    // Return empty observable if no valid course IDs found
    if (courseIds.length === 0) {
      return of<WebinarScheduleResponse[]>([]);
    }

    const env = new EnvService();
    const recordsCount = env.get.brandConfig.appName === 'AceableMortgage' ? 12 : 8;

    // Anchor the schedule window to the current day so only upcoming sessions are
    // requested (and the `recordsCount` cap isn't spent on stale past events). Truncate
    // to the start of the day, with a 1-day back-buffer, so the value is stable across an
    // SSR render and its client hydration (keeps the TransferState cache key steady) and
    // a session earlier today is never dropped by a server/client timezone boundary.
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    startDate.setDate(startDate.getDate() - 1);

    const callParams = {
      ...this.apiBaseParams,
      courseIds,
      startDate: startDate.toISOString(),
      // Deliberate far-future upper bound so newly added sessions are never cut off by a
      // stale hardcoded cutoff (the previous 2026-09-10 value silently hid them). Revisit before ~2035.
      endDate: '2035-12-31T08:30:00.000-04:00',
      recordsCount,
    };

    const params = this.queryBuilder(callParams);

    const productCategory = productEntry ? this.getProductCategory(productEntry) : undefined;

    return this.httpClient
      .get<WebinarScheduleResponse[]>(this.apiConfig.url + 'class/pre-purchase-schedule?' + params, {
        headers: { Authorization: this.apiConfig.aceauth },
      })
      .pipe(
        map((response: WebinarScheduleResponse[]) => {
          // Handle the response based on whether productCategory is provided
          const webinarResponse: WebinarScheduleResponse[] = response.map((item) => {
            // If productCategory exists, use it to set the category
            if (productCategory) {
              return {
                ...item,
                webinarTitle: webinarTitle ?? '',
                webinarContent: webinarContent ?? '',
                category: {
                  code: productCategory[0]?.aceCategoryCode,
                  title: productCategory[0]?.title,
                },
              };
            } else {
              // Preserve the existing category or set a default if none exists
              return {
                ...item,
                webinarTitle: webinarTitle ?? '',
                webinarContent: webinarContent ?? '',
                category: item.category,
              };
            }
          });

          return webinarResponse;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError(() => {
          // The webinar schedule is non-critical: a failure must not take down the host component,
          // so swallow the error and degrade to "no sessions". Deliberately not logged — `no-console`
          // is an error-level rule here, and routing a routinely-empty third-party schedule through
          // the global error handler would page on noise.
          return of<WebinarScheduleResponse[]>([]);
        }),
      );
  }

  public getUserByTransferToken(token: string): Observable<TransferTokenReponse> {
    const requestBody = {
      ...this.apiBaseParams,
      transferToken: token,
    };

    return this.httpClient
      .post<TransferTokenReponse>(this.apiConfig.url + `purchase-receipt/transfer-token`, requestBody, {
        headers: {
          Authorization: this.apiConfig.aceauth,
        },
        withCredentials: true,
      })
      .pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError(this.handleError<TransferTokenReponse>({} as TransferTokenReponse)),
      );
  }

  public getSyncUser(): Observable<SyncUserResponse> {
    return this.httpClient
      .get<SyncUserResponse>(
        this.apiConfig.url + 'user/sync-marketing?fields=initials,adaToken,ambassadorUrl,email&platform=WEB',
        {
          headers: {
            Authorization: this.apiConfig.aceauth,
          },
          withCredentials: true,
        },
      )
      .pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError((error: Partial<HandleErrorError> & { status?: number }) => {
          // Silently handle 401 errors (user not logged in) - this is expected behavior
          // Don't log to console or Sentry for 401s
          if (error?.status === 401) {
            return of({} as SyncUserResponse);
          }
          // For other errors, use the standard error handler
          return this.handleError<SyncUserResponse>({} as SyncUserResponse)(error);
        }),
      );
  }

  /**
   * COUPON OVERHAUL: Enhances price set list with comprehensive coupon collection
   *
   * This method automatically collects coupons from all sources (URL, cookies, banners, etc.)
   * and adds them to existing coupon lists in every multiprice call.
   */
  private enhancePriceSetListWithAllCoupons(priceSetList: ProductCodeLists[]): ProductCodeLists[] {
    // If coupon aggregation service isn't available, return original price set
    if (!this.couponAggregationService) {
      return priceSetList;
    }

    // Collect all potential coupons from various sources
    // NOTE: This call does NOT include craft products - those need to be passed from components!
    const couponCollectionRequest = this.couponAggregationService.collectAllCoupons();
    const allAvailableCoupons = this.couponAggregationService.getAllCouponCodes(couponCollectionRequest);
    const currentUrl = couponCollectionRequest.url; // Get URL for ambassador coupon tracking

    // Enhance each price set with additional coupons and URL context
    return priceSetList.map((priceSet) => {
      const existingCoupons = priceSet.couponCodeList || [];
      const combinedCoupons = [...new Set([...existingCoupons, ...allAvailableCoupons])];

      return {
        ...priceSet,
        couponCodeList: combinedCoupons, // Merge existing + all available coupons
        url: currentUrl, // Include URL for ambassador coupon tracking
      };
    });
  }
}
